'use client'

import { Button } from "@/components/elements/Button"
import Input from "@/components/elements/Input"
import Modal from "@/components/elements/Modal"
import Select from "@/components/elements/Select"
import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "@/components/elements/EmptyState"
import Tooltip from "@/components/elements/Tooltip"
import { Check, Loader2, MapPinHouse, Pencil, Plus, Star, Trash } from "lucide-react"
import { useState } from "react"
import { toast } from "@/lib/toast"
import AddressEdit from "./AddressEdit"
import { getAddressTypeBadge, getTextColorStyles, getVendorPrimaryColor } from "@/utils/util"
import { formatAddressDetails } from "@/utils/order"
import Tabs from "@/components/elements/Tabs"
import { useAddressForm, useAddressManagement } from "@/lib/commerce/hooks"

export default function AddressList({ addressList, addressField, vendor }: { addressList: any, addressField: any, vendor: any }) {
  const address = addressList || []
  const [isLoading, setIsLoading] = useState(addressList === undefined || addressField === undefined)
  if (isLoading && addressList !== undefined && addressField !== undefined) setIsLoading(false)

  const form = useAddressForm({ addressField, vendor })
  const mgmt = useAddressManagement()

  const handleAdd = async () => {
    const v = form.validate()
    if (!v.ok) return toast.error(v.error)
    const ok = await mgmt.addAddress(form.compose())
    if (ok) {
      mgmt.setOpenAdd(false)
      form.reset()
    }
  }

  if (isLoading) return <AddressListSkeleton />

  const primaryColor = getVendorPrimaryColor(vendor)
  const { className: textColorClass, style: textColorStyle } = getTextColorStyles(primaryColor)

  return (
    <>
      <div className="cont py-4 sm:py-8">
        <div className="flex flex-col gap-y-4">
          <div className="flex justify-between items-center gap-4">
            <div className="flex flex-col gap-1">
              <div className="font-semibold text-lg text-foreground">Хаягууд</div>
              <div className="text-sm text-muted-foreground">Таны хүргэлт авах байршил хаягууд</div>
            </div>
            <div>
              <Button icon={Plus} onClick={() => mgmt.setOpenAdd(true)} color={vendor?.color?.primary}>
                Хаяг нэмэх
              </Button>
            </div>
          </div>
          {address && address.length > 0 ? (
            <div className="flex flex-col divide-y divide-y-muted-foreground">
              {address.map((a: any) => (
                <div key={a.id} className="flex justify-between items-center gap-3 py-5">
                  <div className="flex flex-col gap-1">
                    <div className="w-fit mb-1">{getAddressTypeBadge(a.type || 'apartment')}</div>
                    <div className="text-sm text-foreground font-medium">
                      {`${a?.city}, ${a?.duureg}, ${a?.khoroo}, ${formatAddressDetails(a)}`}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {a?.additionalAddress ? a?.additionalAddress : "Нэмэлт мэдээлэл байхгүй"}
                    </div>
                  </div>
                  <div className="flex items-center gap-x-2">
                    {(() => {
                      const isDefaulting = mgmt.defaultingId === a.id
                      const isDeleting = mgmt.deletingId === a.id
                      const rowBusy = isDefaulting || isDeleting
                      const disabledClass = rowBusy ? 'opacity-50 pointer-events-none' : ''
                      return (
                        <>
                          {!a.isDefault ? (
                            <div onClick={() => !rowBusy && mgmt.setDefault(a.id)} className={`inline-flex items-center text-sm justify-center cursor-pointer font-medium transition-colors hover:bg-accent text-accent-foreground h-9 px-3 rounded-md ${disabledClass}`}>
                              <Tooltip content="Үндсэн хаяг болгох" color={vendor?.color?.primary}>
                                {isDefaulting ? (
                                  <Loader2 className={`w-5 h-5 ${textColorClass} animate-spin`} style={textColorStyle} />
                                ) : (
                                  <Star className={`w-5 h-5 ${textColorClass} cursor-pointer`} style={textColorStyle} />
                                )}
                              </Tooltip>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <Check className="w-5 h-5 text-green-500" />
                              <div className={`text-xs ${textColorClass} font-medium`} style={textColorStyle}>Үндсэн</div>
                            </div>
                          )}
                          <div onClick={() => !rowBusy && mgmt.startEdit(a)} className={`inline-flex items-center text-sm justify-center cursor-pointer font-medium transition-colors hover:bg-accent text-accent-foreground h-9 px-3 rounded-md ${disabledClass}`}>
                            <Tooltip content="Хаягийн мэдээлэл засах" color={vendor?.color?.primary}>
                              <Pencil className={`w-5 h-5 ${textColorClass} cursor-pointer`} style={textColorStyle} />
                            </Tooltip>
                          </div>
                          <div onClick={() => !rowBusy && mgmt.removeAddress(a.id)} className={`inline-flex items-center text-sm justify-center cursor-pointer font-medium transition-colors hover:bg-accent text-accent-foreground h-9 px-3 rounded-md ${disabledClass}`}>
                            <Tooltip content="Хаяг устгах" color={vendor?.color?.primary}>
                              {isDeleting ? (
                                <Loader2 className={`w-5 h-5 ${textColorClass} animate-spin`} style={textColorStyle} />
                              ) : (
                                <Trash className={`w-5 h-5 ${textColorClass} cursor-pointer`} style={textColorStyle} />
                              )}
                            </Tooltip>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={MapPinHouse} title="Хаяг байхгүй байна" description="Таны нэмсэн шинэ хаягууд энд харагдах болно." />
          )}
        </div>
      </div>

      <Modal open={mgmt.openAdd} setOpen={mgmt.setOpenAdd} saveAction={handleAdd} isSaveActive={!mgmt.submitting} showCancel={false} isLoading={mgmt.submitting} saveText={mgmt.submitting ? "Хаягийг хадгалж байна..." : "Хадгалах"} title="Шинэ хаяг оруулах" description="" border={vendor?.border} color={vendor?.color?.primary}>
        <AddressFormFields form={form} vendor={vendor} />
      </Modal>

      <AddressEdit open={mgmt.openEdit} setOpen={mgmt.setOpenEdit} address={mgmt.addressToEdit} addressField={addressField} vendor={vendor} updateAddress={mgmt.updateAddress} submitting={mgmt.submitting} />
    </>
  )
}

export function AddressFormFields({ form, vendor }: { form: ReturnType<typeof useAddressForm>, vendor: any }) {
  return (
    <div className="flex flex-col gap-4">
      <Tabs tabs={[{ label: 'Орон сууц', value: 'apartment', content: null }, { label: 'Хашаа байшин', value: 'house', content: null }, { label: 'Оффис', value: 'office', content: null }]} defaultValue={form.addressType} onValueChange={(value: string) => form.setAddressType(value as any)} className="w-full" />
      <Select label="Хот / Аймаг" items={form.provinces} value={form.selectedProvince || ''} onChange={form.onProvinceChange} required />
      <Select label="Дүүрэг / Сум" value={form.selectedDistrict || ''} items={form.districts} required onChange={form.onDistrictChange} />
      <Select label="Хороо / Баг" value={form.selectedSubdistrict || ''} items={form.subdistricts} required onChange={form.onSubdistrictChange} />
      {form.addressType === 'apartment' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Байр" placeholder="Байрны дугаарыг оруулна уу" value={form.bair} onChange={(e: any) => form.setBair(e.target.value)} required color={vendor?.color?.primary} />
          <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={form.toot} onChange={(e: any) => form.setToot(e.target.value)} required color={vendor?.color?.primary} />
        </div>
      )}
      {form.addressType === 'house' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Хашааны дугаар" placeholder="Хашааны дугаар оруулна уу" value={form.houseNum} onChange={(e: any) => form.setHouseNum(e.target.value)} required color={vendor?.color?.primary} />
          <Input label="Гудамж" placeholder="Гудамжны нэр" value={form.street} onChange={(e: any) => form.setStreet(e.target.value)} color={vendor?.color?.primary} />
        </div>
      )}
      {form.addressType === 'office' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={form.building} onChange={(e: any) => form.setBuilding(e.target.value)} required color={vendor?.color?.primary} />
            <Input label="Давхар" placeholder="Давхар" value={form.floor} onChange={(e: any) => form.setFloor(e.target.value)} required color={vendor?.color?.primary} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={form.toot} onChange={(e: any) => form.setToot(e.target.value)} required color={vendor?.color?.primary} />
            <Input label="Оффисын нэр" placeholder="Оффисын нэр (заавал биш)" value={form.officeName} onChange={(e: any) => form.setOfficeName(e.target.value)} color={vendor?.color?.primary} />
          </div>
        </>
      )}
      <Input label="Нэмэлт мэдээлэл" placeholder="Нэмэлт мэдээлэл оруулна уу" value={form.additionalAddress} onChange={(e: any) => form.setAdditionalAddress(e.target.value)} description="Орц, давхар, орцны код гэх мэт" color={vendor?.color?.primary} />
    </div>
  )
}

function AddressListSkeleton() {
  return (
    <div className="cont py-8">
      <div className="flex flex-col gap-y-6">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <Skeleton className="w-20 h-7" />
            <Skeleton className="w-64 h-5" />
          </div>
          <div>
            <Skeleton className="w-28 h-10" />
          </div>
        </div>
        <div className="flex flex-col divide-y divide-y-muted-foreground">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex justify-between items-center py-5">
              <div className="flex flex-col gap-1">
                <Skeleton className="w-80 h-5" />
                <Skeleton className="w-48 h-4" />
              </div>
              <div className="flex items-center gap-x-2">
                <Skeleton className="w-9 h-9" />
                <Skeleton className="w-9 h-9" />
                <Skeleton className="w-9 h-9" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
